import React, { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import HeaderBar from "../components/HeaderBar";
import getCurrentSkin from "../utils/getCurrentSkin";
import { ThemeContext } from "../context/ThemeContext";
import AdminLayoutWrapper from "../components/AdminLayoutWrapper";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaPlus, FaSearch, FaTrash, FaChevronDown, FaChevronUp, FaTimes, FaExclamationTriangle } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

function getFinYearId() {
  try {
    const direct = localStorage.getItem("finYearId");
    if (direct) return String(direct);
    const admin = JSON.parse(localStorage.getItem("admin") || "null");
    if (admin?.finYearId) return String(admin.finYearId);
  } catch (_) {}
  return "";
}

function tenantHeaders() {
  const finYearId = getFinYearId();
  return finYearId ? { "x-fin-year-id": finYearId } : {};
}

function tenantParams(extra = {}) {
  const finYearId = getFinYearId();
  return finYearId ? { ...extra, finYearId } : extra;
}

const UserSetupMasterListing = () => {
  const BASE_URL = "https://nissi-india.com";

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  // UI state
  const [showFilters, setShowFilters] = useState(true);

  // Filters (ASPX)
  const [moduleName, setModuleName] = useState("ERP");
  const [filterUserTypeId, setFilterUserTypeId] = useState("Select");
  const [filterBranchId, setFilterBranchId] = useState("Select");
  const [filterDeptId, setFilterDeptId] = useState("Select");

  // Dropdown data
  const [userTypes, setUserTypes] = useState([]);
  const [branches, setBranches] = useState([]);
  const [depts, setDepts] = useState([]);

  // List
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination
  const recordsPerPage = 10;
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);

  // Delete confirm
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState([]);

  const loadDropdowns = async () => {
    try {
      const [ut, br, dp] = await Promise.all([
        axios.get(`${BASE_URL}/api/userSetup/ddl/usertypes`, {
          headers: tenantHeaders(),
          params: tenantParams(),
        }),
        axios.get(`${BASE_URL}/api/userSetup/ddl/branches`, {
          headers: tenantHeaders(),
          params: tenantParams(),
        }),
        axios.get(`${BASE_URL}/api/userSetup/ddl/departments`, {
          headers: tenantHeaders(),
          params: tenantParams(),
        }),
      ]);

      if (ut.data?.success) setUserTypes(ut.data.list || []);
      if (dp.data?.success) setDepts(dp.data.list || []);
      if (br.data?.success) setBranches(br.data.list || []);
    } catch (err) {
      console.error("dropdown load error:", err);
      toast.error("Failed to load filter dropdowns");
    }
  };

  const normalizedBranches = useMemo(() => {
    return (branches || [])
      .map((b) => {
        const id = b.fld_branchid ?? b.fld_Branchid ?? b.branchid ?? b.id;
        const name = b.fld_branchname ?? b.fld_BranchName ?? b.branchname ?? b.name;
        return { id: String(id ?? ""), name: String(name ?? "") };
      })
      .filter((x) => x.id && x.name);
  }, [branches]);

  const fetchList = async (page = 1) => {
    try {
      setLoading(true);

      const params = tenantParams({
        module: moduleName,
        page,
        limit: recordsPerPage,
        userTypeId: filterUserTypeId,
        branchId: filterBranchId,
        deptId: filterDeptId,
      });

      const res = await axios.get(`${BASE_URL}/api/userSetup/list`, {
        headers: tenantHeaders(),
        params,
      });

      if (!res.data?.success) {
        toast.error(res.data?.message || "Failed to load list");
        return;
      }

      const list = res.data.list || [];
      const p = res.data.pagination || {};

      const safePage = p.page || page;
      const safeLimit = p.limit || recordsPerPage;

      const mapped = list.map((row, index) => ({
        id: row.usersetupid,
        sNo: (safePage - 1) * safeLimit + (index + 1),
        usertype: row.usertype || "",
        department: row.department || "",
        branch: row.branch || "",
        username: row.username || "",
        selected: false,
      }));

      setRecords(mapped);
      setCurrentPage(safePage);
      setTotalRecords(p.total || 0);
      setTotalPages(p.totalPages || 1);
    } catch (err) {
      console.error("list error:", err);
      toast.error("Failed to load list");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDropdowns();
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchList(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleName]);

  const handleSearch = () => {
    fetchList(1);
  };

  const handlePageChange = (page) => {
    if (!page || page === "..." || page === currentPage) return;
    fetchList(page);
  };

  const renderPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      for (let i = 1; i <= maxVisible; i++) pages.push(i);
      pages.push("...");
    }
    return pages;
  };

  const handleSelectRecord = (id, checked) => {
    setRecords((prev) => prev.map((r) => (r.id === id ? { ...r, selected: checked } : r)));
  };

  const openDeleteConfirm = () => {
    const ids = records.filter((r) => r.selected).map((r) => r.id);

    if (ids.length === 0) {
      toast.error("Please select any one");
      return;
    }

    setPendingDeleteIds(ids);
    setConfirmOpen(true);
  };

  const closeDeleteConfirm = () => {
    if (isDeleting) return;
    setConfirmOpen(false);
    setPendingDeleteIds([]);
  };

  const confirmDeleteSelected = async () => {
    if (pendingDeleteIds.length === 0) return;

    try {
      setIsDeleting(true);

      const res = await axios.post(
        `${BASE_URL}/api/userSetup/deleteSelected`,
        { ids: pendingDeleteIds },
        { headers: tenantHeaders() }
      );

      if (res.data?.success) {
        toast.success(res.data?.message || "Deleted Successfully");
        setConfirmOpen(false);
        setPendingDeleteIds([]);
        fetchList(1);
      } else {
        toast.error(res.data?.message || "Delete failed");
      }
    } catch (err) {
      console.error("delete error:", err);
      toast.error(err.response?.data?.message || "Cannot delete (may have references)");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleAddNew = () => {
    navigate(`/adminMaster/UserSetupMaster?module=${encodeURIComponent(moduleName)}`);
  };

  const handleEdit = (id) => {
    navigate(`/adminMaster/UserSetupMaster/${id}?module=${encodeURIComponent(moduleName)}`);
  };

  const showUserTypeAndDept = moduleName !== "Tally";

  return (
    <AdminLayoutWrapper>
      <div className={`min-h-screen ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">USER LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                User Listing
              </h1>
              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {totalRecords} Records
              </span>
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-5 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`rounded-xl border mb-6 overflow-hidden ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div className={`${isDark ? "bg-gray-800" : "bg-gray-50"} p-6`}>
                <div className="grid grid-cols-12 gap-4 items-center">
                  <div className="col-span-12 md:col-span-3">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Module Name <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={moduleName}
                      onChange={(e) => setModuleName(e.target.value)}
                      className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                      }`}
                    >
                      <option value="ERP">ERP</option>
                      <option value="Payroll">Payroll</option>
                      <option value="Tally">Tally</option>
                    </select>
                  </div>

                  <div className="col-span-12 md:col-span-3">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      User Type
                    </label>
                    <select
                      value={filterUserTypeId}
                      onChange={(e) => setFilterUserTypeId(e.target.value)}
                      disabled={!showUserTypeAndDept}
                      className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                      } ${!showUserTypeAndDept ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      <option value="Select">Select</option>
                      {userTypes.map((u) => (
                        <option key={u.id} value={String(u.id)}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-12 md:col-span-3">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Branch
                    </label>
                    <select
                      value={filterBranchId}
                      onChange={(e) => setFilterBranchId(e.target.value)}
                      className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                      }`}
                    >
                      <option value="Select">Select</option>
                      {normalizedBranches.map((b) => (
                        <option key={b.id} value={b.id}>
                          {b.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-span-12 md:col-span-3">
                    <label className={`text-sm font-medium ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                      Department
                    </label>
                    <select
                      value={filterDeptId}
                      onChange={(e) => setFilterDeptId(e.target.value)}
                      disabled={!showUserTypeAndDept}
                      className={`mt-1 w-full px-3 py-2 border rounded-lg ${
                        isDark ? "bg-gray-700 border-gray-600 text-white" : "bg-white border-gray-300"
                      } ${!showUserTypeAndDept ? "opacity-60 cursor-not-allowed" : ""}`}
                    >
                      <option value="Select">Select</option>
                      {depts.map((d) => (
                        <option key={d.id} value={String(d.id)}>
                          {d.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="mt-5">
                  <button
                    onClick={handleSearch}
                    className={`inline-flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                  >
                    <FaSearch />
                    Search
                  </button>
                </div>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className={`${buttonClass} text-white`}>
                  <tr>
                    <th className="px-4 py-4 text-center font-semibold border-r border-white/20">S.No</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">UserType</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Department</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">Branch</th>
                    <th className="px-4 py-4 text-left font-semibold border-r border-white/20">UserName</th>
                    <th className="px-4 py-4 text-center font-semibold">Select</th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className={`px-4 py-6 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        Loading...
                      </td>
                    </tr>
                  ) : records.length === 0 ? (
                    <tr>
                      <td colSpan="6" className={`px-4 py-6 text-center ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                        No Records Found
                      </td>
                    </tr>
                  ) : (
                    records.map((r, index) => (
                      <tr
                        key={r.id}
                        className={`transition-colors duration-200 ${
                          index % 2 === 0
                            ? isDark
                              ? "bg-gray-800"
                              : "bg-gray-50"
                            : isDark
                            ? "bg-gray-900"
                            : "bg-white"
                        }`}
                      >
                        <td className={`px-4 py-3 text-sm text-center border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.sNo}
                        </td>
                        <td className={`px-4 py-3 text-sm border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.usertype}
                        </td>
                        <td className={`px-4 py-3 text-sm border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.department}
                        </td>
                        <td className={`px-4 py-3 text-sm border-r border-gray-200 dark:border-gray-700 ${isDark ? "text-gray-300" : "text-gray-900"}`}>
                          {r.branch}
                        </td>
                        <td className={`px-4 py-3 text-sm border-r border-gray-200 dark:border-gray-700 font-medium ${isDark ? "text-blue-300" : "text-blue-600"}`}>
                          <button
                            type="button"
                            onClick={() => handleEdit(r.id)}
                            className="underline hover:opacity-80"
                            title="Click to edit"
                          >
                            {r.username}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <input
                            type="checkbox"
                            checked={!!r.selected}
                            onChange={(e) => handleSelectRecord(r.id, e.target.checked)}
                            className={`w-4 h-4 cursor-pointer ${isDark ? "accent-blue-400" : "accent-blue-600"}`}
                          />
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="flex justify-start mt-6">
            <div className="flex gap-1">
              {renderPageNumbers().map((page, idx) => (
                <button
                  key={idx}
                  onClick={() => handlePageChange(page)}
                  disabled={page === "..."}
                  className={`px-3 py-2 text-sm font-medium rounded border transition-colors duration-200 ${
                    page === currentPage
                      ? "bg-blue-600 text-white border-blue-600"
                      : page === "..."
                      ? isDark
                        ? "bg-gray-800 text-gray-400 border-gray-700 cursor-not-allowed"
                        : "bg-gray-100 text-gray-400 border-gray-300 cursor-not-allowed"
                      : isDark
                      ? "bg-gray-700 text-gray-300 border-gray-600 hover:bg-gray-600"
                      : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                  }`}
                >
                  {page}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={openDeleteConfirm}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                isDark ? "bg-red-700 hover:bg-red-600 text-white" : "bg-red-600 hover:bg-red-700 text-white"
              }`}
            >
              <FaTrash />
              Remove
            </button>
          </div>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>

      {confirmOpen && (
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-confirm-title"
        >
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={closeDeleteConfirm}
          />

          <div
            className={`relative w-full max-w-md rounded-2xl border shadow-2xl overflow-hidden ${
              isDark
                ? "bg-gradient-to-br from-gray-900 to-gray-800 border-white/10 text-white"
                : "bg-white border-gray-200 text-gray-900"
            }`}
          >
            <div className={`h-1.5 w-full ${buttonClass}`} />

            <button
              type="button"
              onClick={closeDeleteConfirm}
              className={`absolute top-3 right-3 p-1.5 rounded-lg transition ${
                isDark ? "hover:bg-white/10 text-white/70" : "hover:bg-gray-100 text-gray-500"
              }`}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-red-600/15 text-red-500">
                  <FaExclamationTriangle className="text-xl" />
                </div>
                <div>
                  <h3 id="delete-confirm-title" className="text-lg font-semibold">
                    Delete User Setup
                  </h3>
                  <p className={`mt-2 text-sm leading-6 ${isDark ? "text-white/70" : "text-gray-600"}`}>
                    Are you sure you want to delete{" "}
                    <span className="font-semibold">
                      {pendingDeleteIds.length} user{pendingDeleteIds.length > 1 ? "s" : ""}
                    </span>
                    ? This action cannot be undone.
                  </p>
                </div>
              </div>

              <div className="mt-6 flex items-center justify-end gap-3">
                <button
                  type="button"
                  onClick={closeDeleteConfirm}
                  disabled={isDeleting}
                  className={`px-4 py-2 rounded-xl text-sm font-medium border transition ${
                    isDark
                      ? "border-white/15 bg-white/5 hover:bg-white/10 text-white"
                      : "border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
                  } disabled:opacity-50`}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmDeleteSelected}
                  disabled={isDeleting}
                  className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium text-white bg-red-600 hover:bg-red-700 shadow transition hover:-translate-y-0.5 disabled:opacity-50 disabled:hover:translate-y-0"
                >
                  <FaTrash />
                  {isDeleting ? "Removing..." : "Remove"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayoutWrapper>
  );
};

export default UserSetupMasterListing;